import {
  LayoutDashboard,
  Bell,
  ClipboardList,
  Sparkles,
  ShoppingBag,
  BarChart3,
  Star,
  Wallet,
  Receipt,
  Banknote,
  ShieldCheck,
  Settings,
  LifeBuoy,
  type LucideIcon,
} from 'lucide-react'
import { tenantName as resolveTenantName } from './tenant'

/**
 * Minimal local shape for the i18next `t` function so this module doesn't
 * depend on `i18next`'s deep types. Matches what `useTranslation()` returns
 * from `@cravo/i18n` for our usage (string key + optional vars).
 */
type TFn = (key: string, vars?: Record<string, unknown>) => string

export interface NavItem {
  key: string
  label: string
  icon: LucideIcon
  path: string
  /**
   * Marks this item as a primary navigation target. Used by `topbar` to
   * decide which items live inline vs collapse into the "More" dropdown.
   * All items are visible in `sidebar` and `sidebar-mini` regardless.
   */
  primary?: boolean
}

export interface NavGroup {
  key: string
  label: string
  items: NavItem[]
}

/**
 * Single source of truth for the vendor portal's primary navigation. All
 * three layout variants (sidebar, sidebar-mini, topbar) read from this so
 * adding or moving a route is a one-line edit here.
 *
 * `t` is the i18next translation function — pass it in from the consumer so
 * the helper is re-runnable on language change without forcing a re-mount.
 *
 * `opts.aiEnabled` gates the AI item — strict: hidden unless the tenant's
 * integrations bootstrap reports `ai.enabled === true`. See useAiEnabled().
 */
export function buildNavGroups(t: TFn, opts: { aiEnabled?: boolean } = {}): NavGroup[] {
  const appName = resolveTenantName()
  const todayItems: NavItem[] = [
    { key: 'dash', label: t('sidebar.items.dashboard'), icon: LayoutDashboard, path: '/', primary: true },
    { key: 'requests', label: t('sidebar.items.requests'), icon: Bell, path: '/requests', primary: true },
    { key: 'orders', label: t('sidebar.items.orders'), icon: ClipboardList, path: '/orders', primary: true },
  ]
  if (opts.aiEnabled) {
    todayItems.push({ key: 'ai', label: t('sidebar.items.ai', { appName }), icon: Sparkles, path: '/ai', primary: true })
  }
  return [
    {
      key: 'today',
      label: t('sidebar.groups.today'),
      items: todayItems,
    },
    {
      key: 'catalog',
      label: t('sidebar.groups.catalog'),
      items: [
        { key: 'menu', label: t('sidebar.items.menu'), icon: ShoppingBag, path: '/menu', primary: true },
        { key: 'analytics', label: t('sidebar.items.analytics'), icon: BarChart3, path: '/analytics' },
        { key: 'feedback', label: t('sidebar.items.feedback'), icon: Star, path: '/feedback' },
      ],
    },
    {
      key: 'finance',
      label: t('sidebar.groups.finance'),
      items: [
        { key: 'wallet', label: t('sidebar.items.wallet'), icon: Wallet, path: '/wallet' },
        { key: 'settlements', label: t('sidebar.items.settlements'), icon: Receipt, path: '/settlements' },
        { key: 'bank', label: t('sidebar.items.bankAndPayouts'), icon: Banknote, path: '/bank' },
        { key: 'kyc', label: t('sidebar.items.kyc', { defaultValue: 'KYC verification' }), icon: ShieldCheck, path: '/kyc' },
      ],
    },
    {
      key: 'explore',
      label: t('sidebar.groups.explore'),
      items: [
        { key: 'settings', label: t('sidebar.items.settings'), icon: Settings, path: '/settings' },
        { key: 'support', label: t('sidebar.items.helpAndSupport'), icon: LifeBuoy, path: '/support' },
      ],
    },
  ]
}

/** Flat list, primary-only. Convenience for layouts that need a quick header bar. */
export function buildPrimaryNavItems(t: TFn, opts: { aiEnabled?: boolean } = {}): NavItem[] {
  return buildNavGroups(t, opts).flatMap((g) => g.items.filter((i) => i.primary))
}
